import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import PulsingIndicator from '../animation/PulsingIndicator';
import { setLogout } from '../../redux/features/authSlice';
import { resetJobState } from '../../redux/features/jobSlice';
import { resetDashboard } from '../../actions/dashboardActions';
import { 
  selectCompanyName
} from '../../utils/selectorUtils';
import { getRoutePath, getDashboardHomePath, getCompanySlugFromUrl, isSuperAdminUser } from '../../utils/routingUtils';
import { isFeatureAllowed, getPackageRestrictionMessage, isMarketingChannelAllowed } from '../../utils/packageLimits';
import { mergeAuthProfile } from '../../utils/mergeAuthProfile';
import { getAiUsageStatus as fetchAiUsageStatus, getUnreadNotificationCount, getWorkloadJobCount, getKvarxMarketplaceStats } from '../../redux/api';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { companyHasKvarxCity } from '../../utils/kvarxCity';
import { connectKvarxMarketSocket } from '../../utils/kvarxRealtime';
import {
  alertJobRemoved,
  alertNewJob,
  alertOfferAccepted,
  alertOfferExpired,
  isMyCompany,
  unlockKvarxAudio,
} from '../../utils/kvarxAlerts';
import { countWorkloadJobs, countWorkloadPeople, formatRotationSeconds, getMonkeyRotationDuration, getWorkloadPerPerson } from '../../utils/monkeyRotation';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import useMonkeySpinPaused from '../../hooks/useMonkeySpinPaused';
import {
  Briefcase,
  Zap,
  Wrench,
  Archive,
  Package,
  FileText,
  Calculator,
  UserPlus as WorkerIcon,
  Building2,
  Gift,
  Award,
  Star,
  Crown,
  Settings,
  ShoppingBag,
  Layout,
  User,
  ChevronDown,
  Activity,
  Brain,
  Phone,
  StickyNote,
  Mail,
  MessageSquare,
  MessageCircle,
  ChevronRight,
  Video,
  Image as ImageIcon,
  Megaphone,
  BarChart3,
  Bell,
  ClipboardList,
  LogOut,
  ArrowDownCircle,
  ArrowUpCircle,
  RotateCcw,
  Car,
  Undo2,
  FileMinus,
  MoreHorizontal
} from 'lucide-react';
import NotificationBell from '../notifications/NotificationBell';
import SpinTaskerLogo from '../shared/SpinTaskerLogo';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    action: true,
    company: false,
    marketing: false,
    tutorijali: false,
    package: false,
    profile: false,
    payments: false,
    ai: false,
    kvarx: false,
    roba: false,
    vehicles: false,
    warehouse: true,
    spareParts: false,
    assets: false,
    ostalo: false
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const companyName = useSelector(selectCompanyName);
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin' || isSuperAdminUser();
  const { jobs, lastFetchConfig } = useSelector((state) => state.job || { jobs: [], lastFetchConfig: null });
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const [apiWorkloadCount, setApiWorkloadCount] = useState(null);
  const [monkeySpinPaused] = useMonkeySpinPaused();
  
  // Global settings for AI and Parking - saved in localStorage (only for workers)
  const [globalUseAI, setGlobalUseAI] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('worker_global_useAI') === 'true';
  });
  const [globalEnableParking, setGlobalEnableParking] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('worker_global_enableParking') === 'true';
  });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  
  // Save global settings to localStorage
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('worker_global_useAI', globalUseAI.toString());
    }
  }, [globalUseAI]);
  
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('worker_global_enableParking', globalEnableParking.toString());
    }
  }, [globalEnableParking]);

  React.useEffect(() => {
    let mounted = true;
    const loadUnread = async () => {
      try {
        const response = await getUnreadNotificationCount();
        if (mounted) {
          setUnreadNotifications(response?.data?.unreadCount || 0);
        }
      } catch (e) {
        if (mounted) setUnreadNotifications(0);
      }
    };

    loadUnread();
    const timer = setInterval(loadUnread, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);
  
  // Check if current user is a worker with limited permissions (only canViewAllJobs, not full admin)
  const userTypeFromStorage = localStorage.getItem('userType');
  const workerPermissions = JSON.parse(localStorage.getItem('workerPermissions') || '{}');
  const canViewAllJobs = workerPermissions.canViewAllJobs;
  const canEditJobs = workerPermissions.canEditJobs;
  const canDeleteJobs = workerPermissions.canDeleteJobs;
  const canEditClients = workerPermissions.canEditClients;
  const canViewWorkers = workerPermissions.canViewWorkers;
  const canViewClients = workerPermissions.canViewClients;
  const hasFullAdminAccess = canViewAllJobs && canEditJobs && canDeleteJobs && canEditClients;
  const isWorker = userTypeFromStorage === 'worker';
  const isCompanyUser = !isWorker; // svi koji nisu radnici (admin/owner) treba da vide sve profile sekcije
  const isWorkerWithLimitedAccess = isWorker && canViewAllJobs && !hasFullAdminAccess;
  // Worker with only assigned jobs access (no canViewAllJobs, no canViewWorkers, no canViewClients)
  const hasOnlyAssignedJobsAccess = isWorker && !canViewAllJobs && !canViewWorkers && !canViewClients;

  const handleNavItemSelect = React.useCallback((event) => {
    if (!sidebarOpen) return;
    const anchor = event.target.closest('a');
    if (anchor) {
      setSidebarOpen(false);
    }
  }, [sidebarOpen]);

  const [aiUsage, setAiUsage] = useState({ used: 0, limit: 0, hardLimit: 1000 });
  const [kvarxCounts, setKvarxCounts] = useState({ matched: 0, total: 0 });
  const [kvarxUnlocked, setKvarxUnlocked] = useState(() => companyHasKvarxCity(user));
  const [kvarxPing, setKvarxPing] = useState(false);
  
  const effectiveUser = useMemo(() => mergeAuthProfile(user), [user]);
  
  // Log the user data to check country code / company name
  // try {
  //   console.log("AdminLayout user data:", {
  //     user,
  //     effectiveUser,
  //     rawCountryCode: effectiveUser?.result?.countryCode,
  //     profile: typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('profile') || 'null') : null
  //   });
  // } catch (e) {
  //   console.error('AdminLayout: error logging profile info:', e);
  // }
  
  // Ensure country code is properly formatted (fallback na BA)
  const countryCode = (effectiveUser?.result?.countryCode || 'ba')?.toLowerCase();
  
  // console.log("AdminLayout: using country code for flag:", countryCode);

  // Ime kompanije za sidebar – koristi effectiveUser, pa tek onda selector
  const sidebarCompanyName = useMemo(() => {
    if (effectiveUser?.result?.companyName) {
      return effectiveUser.result.companyName;
    }

    // Ako selector već ima smislen naziv (ne default), koristi njega
    if (companyName && companyName !== 'Vaša Kompanija') {
      return companyName;
    }

    return 'Vaša Kompanija';
  }, [effectiveUser, companyName]);
  
  const workloadCountFromJobs = React.useMemo(() => countWorkloadJobs(jobs), [jobs]);
  const workforce = React.useMemo(
    () => countWorkloadPeople(workers, partners),
    [workers, partners]
  );

  React.useEffect(() => {
    dispatch(getWorkers());
    dispatch(getPartners());
  }, [dispatch]);

  React.useEffect(() => {
    let mounted = true;
    const loadWorkloadCount = async () => {
      try {
        const response = await getWorkloadJobCount(getBusinessType());
        if (mounted) {
          const count = Number(response?.data?.count);
          setApiWorkloadCount(Number.isFinite(count) ? count : 0);
        }
      } catch (e) {
        if (mounted) {
          setApiWorkloadCount((prev) => (prev == null ? workloadCountFromJobs : prev));
        }
      }
    };

    loadWorkloadCount();
    const timer = setInterval(loadWorkloadCount, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [workloadCountFromJobs]);

  // Ukupno otvorenih poslova (kalendar + nezakazani + prosleđeni).
  // Ako je učitan ceo skup, brojimo iz Redux-a; inače koristimo poseban count sa servera.
  const workloadJobCount = lastFetchConfig?.fetchAll
    ? workloadCountFromJobs
    : (apiWorkloadCount ?? workloadCountFromJobs);

  // 30 poslova po osobi = 30s po krugu, 60 = 15s, 15 = 60s → period = 900 / (poslovi / ljudi)
  const rotationDuration = React.useMemo(
    () => getMonkeyRotationDuration(getWorkloadPerPerson(workloadJobCount, workforce.people)),
    [workloadJobCount, workforce.people]
  );
  const monkeySpinStyle = monkeySpinPaused
    ? { transform: 'rotate(0deg)', animation: 'none' }
    : { animation: `spin-slow ${rotationDuration}s linear infinite` };
  
  const handleLogout = () => {
    dispatch(resetJobState());
    dispatch(resetDashboard());
    dispatch(setLogout());
    navigate('/auth?role=company&type=login');
  };

  const toggleSection = (section) => {
    setOpenSections(prev => {
      const newState = {
        action: false,
        company: false,
        marketing: false,
        tutorijali: false,
        package: false,
        profile: false,
        payments: false,
        ai: false,
        kvarx: false,
        roba: false,
        vehicles: false,
        warehouse: prev.warehouse,
        spareParts: prev.spareParts,
        assets: prev.assets,
        ostalo: prev.ostalo
      };
      // Only toggle the clicked section if it was previously closed
      newState[section] = !prev[section];
      if (section === 'company' && newState.company) {
        newState.warehouse = true;
        newState.spareParts = false;
        newState.assets = false;
        newState.ostalo = false;
      }
      return newState;
    });
  };

  const toggleRobaCluster = (cluster) => {
    setOpenSections((prev) => {
      const opening = !prev[cluster];
      return {
        ...prev,
        warehouse: cluster === 'warehouse' && opening,
        spareParts: cluster === 'spareParts' && opening,
        assets: cluster === 'assets' && opening,
        ostalo: cluster === 'ostalo' && opening,
      };
    });
  };

  const isLinkActive = (path) => {
    const companySlug = getCompanySlugFromUrl(location.pathname);
    const currentPath = location.pathname;
    
    // Handle dashboard home
    if (path === '/dashboard') {
      if (companySlug) {
        return currentPath === `/${companySlug}/dashboard` || currentPath === `/${companySlug}/dashboard/`;
      }
      return currentPath === '/dashboard' || currentPath === '/dashboard/';
    }
    
    // Handle other paths - remove /dashboard/ prefix if present
    const cleanPath = path.replace('/dashboard/', '');
    const expectedPath = getRoutePath(cleanPath, location.pathname);
    
    // Check both slug format and legacy format
    return currentPath === expectedPath || 
           currentPath === path ||
           (companySlug && currentPath === `/${companySlug}/dashboard/${cleanPath}`) ||
           (!companySlug && currentPath === `/dashboard/${cleanPath}`);
  };

  React.useEffect(() => {
    const warehouseActive = [
      '/dashboard/company/inventory',
      '/dashboard/company/inventory/inputs',
      '/dashboard/company/inventory/withdrawn',
      '/dashboard/company/inventory/reversi',
    ].some((path) => isLinkActive(path));
    const sparePartsActive = [
      '/dashboard/spare-parts',
      '/dashboard/spare-parts/inputs',
      '/dashboard/spare-parts/outputs',
    ].some((path) => isLinkActive(path));
    const assetsActive = [
      '/dashboard/company/inventar',
      '/dashboard/company/inventar/inputs',
      '/dashboard/company/inventar/withdrawn',
    ].some((path) => isLinkActive(path));
    const ostaloActive = [
      '/dashboard/company/inventory/customs',
      '/dashboard/company/inventory/calculations',
      '/dashboard/vehicles',
    ].some((path) => isLinkActive(path));
    if (warehouseActive || sparePartsActive || assetsActive || ostaloActive) {
      setOpenSections((prev) => {
        const next = {
          ...prev,
          company: true,
          warehouse: warehouseActive,
          spareParts: sparePartsActive,
          assets: assetsActive,
          ostalo: ostaloActive,
        };
        if (
          next.company === prev.company &&
          next.warehouse === prev.warehouse &&
          next.spareParts === prev.spareParts &&
          next.assets === prev.assets &&
          next.ostalo === prev.ostalo
        ) {
          return prev;
        }
        return next;
      });
    }
  }, [location.pathname]);

  const SidebarNavGroup = ({ children }) => (
    <div
      className="px-4 pt-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wider first:pt-1"
      style={{ color: 'var(--nav-text)', opacity: 0.45 }}
    >
      {children}
    </div>
  );

  const SidebarNavCluster = ({ title, to, activePath, icon, disabled, disabledTitle, children, collapsible = false, open: openProp, onToggle }) => {
    const childActive = React.Children.toArray(children).some(
      (child) => child?.props?.activePath && isLinkActive(child.props.activePath)
    );
    const titleActive = Boolean(to && activePath && isLinkActive(activePath));
    const open = collapsible ? Boolean(openProp) : true;
    const highlightParent = titleActive || (collapsible && childActive && !open);
    const headerClass = `flex items-center w-full py-1.5 px-4 rounded transition duration-200 ${
      disabled ? 'opacity-50 grayscale cursor-not-allowed' : ''
    }`;
    const headerStyle = {
      backgroundColor: highlightParent ? 'var(--nav-active-bg)' : 'transparent',
      color: highlightParent ? 'var(--nav-active-text)' : 'var(--nav-text)',
    };

    return (
      <div className="pt-2 first:pt-0">
        {collapsible ? (
          <button
            type="button"
            onClick={() => {
              if (!disabled) onToggle?.();
            }}
            className={headerClass}
            style={headerStyle}
            title={disabled ? (disabledTitle || 'Nije dostupno u ovom paketu') : undefined}
          >
            {icon}
            <span className="font-medium flex-1 text-left text-[15px] leading-tight">{title}</span>
            {open ? <ChevronDown className="h-3.5 w-3.5 ml-1 shrink-0 opacity-70" /> : <ChevronRight className="h-3.5 w-3.5 ml-1 shrink-0 opacity-70" />}
          </button>
        ) : (
          <SidebarNavItem
            to={to}
            activePath={activePath}
            icon={icon}
            disabled={disabled}
            disabledTitle={disabledTitle}
          >
            <span className="font-medium text-[15px] leading-tight">{title}</span>
          </SidebarNavItem>
        )}
        {open && (
          <div className="ml-3 pl-2 border-l space-y-0.5 text-[15px]" style={{ borderColor: 'var(--nav-border, rgba(255,255,255,0.12))' }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  const SidebarNavItem = ({ to, activePath, icon, children, disabled, disabledTitle, variant = 'child' }) => {
    const isActive = isLinkActive(activePath);
    const isCluster = variant === 'cluster';
    const className = `flex items-center ${isCluster ? 'py-1.5' : 'py-2'} px-4 rounded transition duration-200 ${
      disabled ? 'opacity-50 grayscale cursor-not-allowed' : ''
    }`;

    const style = {
      backgroundColor: isActive ? 'var(--nav-active-bg)' : 'transparent',
      color: isActive ? 'var(--nav-active-text)' : 'var(--nav-text)',
    };

    const label = isCluster
      ? <span className="font-medium text-[15px] leading-tight">{children}</span>
      : children;

    if (disabled) {
      return (
        <div className={className} style={style} title={disabledTitle || 'Nije dostupno u ovom paketu'}>
          {icon}
          {label}
        </div>
      );
    }

    return (
      <Link to={to} className={className} style={style}>
        {icon}
        {label}
      </Link>
    );
  };
  
  // Create wrapper functions that use current location
  const getRoutePathWithLocation = (path) => {
    const routePath = getRoutePath(path, location.pathname);
    // console.log(`🔗 getRoutePathWithLocation('${path}') = ${routePath}`);
    return routePath;
  };
  const getDashboardHomePathWithLocation = () => getDashboardHomePath(location.pathname);

  React.useEffect(() => {
    let cancelled = false;

    const loadAiUsage = async () => {
      try {
        const res = await fetchAiUsageStatus();
        if (!cancelled && res?.data) {
          setAiUsage({
            used: Number(res.data.used || 0),
            limit: Number(res.data.limit || 0),
            hardLimit: Number(res.data.hardLimit || 1000)
          });
        }
      } catch (e) {
        if (!cancelled) {
          let pkg = Number(user?.result?.package || user?.package || 1);
          try {
            const profile = JSON.parse(localStorage.getItem('profile') || '{}');
            if (profile?.result?.package != null) {
              pkg = Number(profile.result.package);
            }
          } catch {
            // ignore parse errors
          }
          const fallbackLimit = pkg === 2 ? 100 : pkg === 3 ? 300 : (pkg === 4 || pkg === 5 ? 500 : 0);
          setAiUsage((prev) => ({ ...prev, limit: fallbackLimit }));
        }
      }
    };

    loadAiUsage();
    const intervalId = setInterval(loadAiUsage, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user]);

  React.useEffect(() => {
    if (companyHasKvarxCity(effectiveUser)) {
      setKvarxUnlocked(true);
    }
  }, [effectiveUser]);

  React.useEffect(() => {
    if (isSuperAdmin || isWorkerWithLimitedAccess || hasOnlyAssignedJobsAccess) return undefined;

    let cancelled = false;
    const isOnKvarxPage = String(location.pathname || '').includes('/dashboard/kvarx');

    const applyCounts = (matched, total) => {
      if (cancelled) return;
      setKvarxCounts({
        matched: Number(matched || 0),
        total: Number(total || 0),
      });
    };

    const loadKvarxStats = async () => {
      try {
        const res = await getKvarxMarketplaceStats();
        if (!cancelled && res?.data) {
          if (typeof res.data.available === 'boolean') {
            setKvarxUnlocked(res.data.available);
          }
          applyCounts(res.data.matched, res.data.total);
        }
      } catch {
        if (!cancelled) applyCounts(0, 0);
      }
    };

    const onFilterCounts = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      applyCounts(detail.matched, detail.total);
    };

    window.addEventListener('kvarx-nav-counts', onFilterCounts);

    loadKvarxStats();
    let intervalId = null;
    if (!isOnKvarxPage) {
      intervalId = setInterval(loadKvarxStats, 60000);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('kvarx-nav-counts', onFilterCounts);
    };
  }, [isSuperAdmin, isWorkerWithLimitedAccess, hasOnlyAssignedJobsAccess, location.pathname]);

  React.useEffect(() => {
    if (isSuperAdmin || isWorkerWithLimitedAccess || hasOnlyAssignedJobsAccess) {
      return undefined;
    }
    const unlock = () => unlockKvarxAudio();
    window.addEventListener('pointerdown', unlock);
    unlock();
    const socket = connectKvarxMarketSocket();

    const onJob = (payload) => {
      if (payload?.deleted) {
        alertJobRemoved();
        setKvarxPing(true);
        return;
      }
      if (payload?.job) {
        alertNewJob(payload.job);
        setKvarxPing(true);
        setKvarxCounts((prev) => ({
          matched: prev.matched + 1,
          total: prev.total + 1,
        }));
      }
    };

    const onOffer = (payload) => {
      const mine = isMyCompany(payload?.companyId);
      if (payload?.expired) {
        alertOfferExpired(mine);
        return;
      }
      if (payload?.withdrawn) return;
      if (payload?.kind === 'accepted' || payload?.jobStatus === 'closed' || payload?.jobStatus === 'agreed') {
        alertOfferAccepted(isMyCompany(payload?.companyId), payload?.jobId);
        setKvarxPing(true);
      }
    };

    socket.on('kvarx_job', onJob);
    socket.on('kvarx_offer', onOffer);

    return () => {
      window.removeEventListener('pointerdown', unlock);
      socket.off('kvarx_job', onJob);
      socket.off('kvarx_offer', onOffer);
    };
  }, [isSuperAdmin, isWorkerWithLimitedAccess, hasOnlyAssignedJobsAccess]);

  const aiUsageTextColorClass = React.useMemo(() => {
    const used = Number(aiUsage.used || 0);
    const limit = Number(aiUsage.limit || 0);
    if (limit > 0 && used >= limit * 2) return 'text-red-400';
    if (limit > 0 && used > limit) return 'text-orange-400';
    return 'text-white';
  }, [aiUsage]);
  
  return (
    <div 
      className="flex min-h-screen"
      style={{
        backgroundColor: 'var(--dashboard-bg, #0f172a)',
        color: 'var(--dashboard-text, #e2e8f0)'
      }}
    >
      {/* Sidebar za mobilne - prikazuje se kao overlay */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed inset-0 z-20 transition-opacity bg-black opacity-50 lg:hidden`} 
        onClick={() => setSidebarOpen(false)}></div>
      
      {/* Sidebar */}
      <div 
        className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          fixed inset-y-0 left-0 z-30 w-64 overflow-y-auto transition duration-300 transform 
          lg:translate-x-0 lg:relative lg:inset-0 shadow-lg
        `}
        style={{
          backgroundColor: 'var(--nav-bg, #1e293b)',
          borderRight: '1px solid var(--nav-border, rgba(255, 255, 255, 0.1))'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-6"
          style={{ borderBottom: '1px solid var(--nav-border)' }}
        >
          <div className="flex items-center gap-2">
            <span 
              key={monkeySpinPaused ? 'stopped' : `spin-${rotationDuration}`}
              style={{ 
                color: '#166534', 
                fontSize: '1.33em', 
                marginRight: '4px', 
                fontWeight: 'bold',
                ...monkeySpinStyle
              }} 
              className="text-xl inline-block"
              title={t(
                'dashboard.monkeyWorkload.logoTitle',
                '{{count}} otvorenih poslova · 1 krug za {{seconds}} s',
                {
                  count: workloadJobCount,
                  seconds: formatRotationSeconds(rotationDuration)
                }
              )}
            >
              @
            </span>
            <span style={{ color: 'var(--nav-text)' }} className="text-xl font-semibold">
              {sidebarCompanyName}
            </span>
            <img 
              src={`https://flagcdn.com/${countryCode}.svg`}
              alt={`${countryCode} flag`}
              className="h-4 w-6"
              onError={(e) => {
                console.error("Flag loading error:", {
                  src: e.target.src,
                  countryCode,
                  user: user?.result
                });
                e.target.src = `https://flagcdn.com/ba.svg`; // Fallback to Bosnia flag
              }}
            />
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
            <svg style={{ color: 'var(--nav-text)' }} className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="mt-4 px-4 overflow-y-auto pb-20" onClick={handleNavItemSelect}>
          {/* Dashboard Link - Hidden only for super admin (super admin has Super Admin section) */}
          {!isSuperAdmin && (
            <Link 
              to={getDashboardHomePathWithLocation()} 
              className="flex flex-col py-2.5 px-4 rounded transition duration-200 font-bold"
              style={{
                backgroundColor: isLinkActive('/dashboard') ? 'var(--nav-active-bg)' : 'transparent',
                color: isLinkActive('/dashboard') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                ':hover': {
                  backgroundColor: 'var(--nav-bg-hover)',
                  color: 'var(--nav-text-hover)'
                }
              }}
            >
              <div className="flex items-center">
                <Layout className="h-5 w-5 mr-2" />
                {t('nav.dashboard')}
              </div>
            </Link>
          )}
          
          {/* Action Section - Hidden for super admin, limited for workers with only canViewAllJobs */}
          {!isSuperAdmin && (
            <>
              <button 
                onClick={() => toggleSection('action')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>{t('nav.action')}</span>
                {openSections.action ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.action && (
                <div className="mt-2 space-y-1 pl-2">
              <Link 
                to={getRoutePathWithLocation('jobs')} 
                className={`flex items-center py-2 px-4 rounded transition duration-200 ${hasOnlyAssignedJobsAccess ? '' : ''}`}
                style={{
                  backgroundColor: isLinkActive('/dashboard/jobs') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/jobs') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                {t('nav.jobs')}
              </Link>
              {!isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
                <>
              <Link 
                to={getRoutePathWithLocation('status')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/status') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/status') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Activity className="h-4 w-4 mr-2" />
                {t('nav.status')}
                <PulsingIndicator size={6} color="#2ECC71" className="ml-2" />
              </Link>
              {/* Show Workers link only if user is not a worker OR has canViewWorkers permission */}
              {(!isWorker || canViewWorkers) && !hasOnlyAssignedJobsAccess && (
                <Link 
                  to={getRoutePathWithLocation('workers')} 
                  className="flex items-center py-2 px-4 rounded transition duration-200"
                  style={{
                    backgroundColor: isLinkActive('/dashboard/workers') ? 'var(--nav-active-bg)' : 'transparent',
                    color: isLinkActive('/dashboard/workers') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                    ':hover': {
                      backgroundColor: 'var(--nav-bg-hover)',
                      color: 'var(--nav-text-hover)'
                    }
                  }}
                >
                  <WorkerIcon className="h-4 w-4 mr-2" />
                  {t('nav.workers')}
                </Link>
              )}
              {!hasOnlyAssignedJobsAccess && isFeatureAllowed('partners', user) && (
              <Link 
                to={getRoutePathWithLocation('partners')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/partners') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/partners') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Building2 className="h-4 w-4 mr-2" />
                {t('nav.partners')}
              </Link>
              )}
              {!hasOnlyAssignedJobsAccess && (
              <Link 
                to={getRoutePathWithLocation('services')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/services') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/services') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Wrench className="h-4 w-4 mr-2" />
                {t('nav.services')}
              </Link>
              )}
              {/* Show Clients link only if user is not a worker OR has canViewClients permission */}
              {(!isWorker || canViewClients) && !hasOnlyAssignedJobsAccess && (
                <Link 
                  to={getRoutePathWithLocation('clients')} 
                  className="flex items-center py-2 px-4 rounded transition duration-200"
                  style={{
                    backgroundColor: isLinkActive('/dashboard/clients') ? 'var(--nav-active-bg)' : 'transparent',
                    color: isLinkActive('/dashboard/clients') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                    ':hover': {
                      backgroundColor: 'var(--nav-bg-hover)',
                      color: 'var(--nav-text-hover)'
                    }
                  }}
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('nav.clients')}
                </Link>
              )}
              {!hasOnlyAssignedJobsAccess && (
              <Link 
                to={getRoutePathWithLocation('notifications')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/notifications') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/notifications') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <StickyNote className="h-4 w-4 mr-2" />
                <span>{t('nav.notifications')}</span>
                {unreadNotifications > 0 && (
                  <span className="ml-2 inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-xs text-white">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </Link>
              )}
              {!hasOnlyAssignedJobsAccess && (
              <Link 
                to={getRoutePathWithLocation('archive')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/archive') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/archive') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Archive className="h-4 w-4 mr-2" />
                {t('nav.archive')}
              </Link>
              )}
              {!hasOnlyAssignedJobsAccess && (
              <Link 
                to={getRoutePathWithLocation('analitika')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/analitika') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/analitika') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {t('nav.analytics', 'Analitika')}
              </Link>
              )}
              {/* AI Biznis link je premesten u posebnu AI sekciju ispod Plaćanja */}
                </>
              )}
            </div>
              )}
            </>
          )}
          
          {/* Roba Section - For workers who don't see the Company section */}
          {isWorker && (hasOnlyAssignedJobsAccess || isWorkerWithLimitedAccess) && (
            <>
              <button 
                onClick={() => toggleSection('roba')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>{t('nav.company', 'Roba')}</span>
                {openSections.roba ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.roba && (
                <div className="mt-2 space-y-1 pl-2">
                  <Link 
                    to={getRoutePathWithLocation('company/inventory/reversi')} 
                    className="flex items-center py-2 px-4 rounded transition duration-200"
                    style={{
                      backgroundColor: isLinkActive('/dashboard/company/inventory/reversi') ? 'var(--nav-active-bg)' : 'transparent',
                      color: isLinkActive('/dashboard/company/inventory/reversi') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                      ':hover': {
                        backgroundColor: 'var(--nav-bg-hover)',
                        color: 'var(--nav-text-hover)'
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('nav.reversi', 'Reversi')}
                  </Link>
                </div>
              )}
            </>
          )}
          
          {/* Company Section (Roba) - Always visible, but links can be disabled by package */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
            onClick={() => toggleSection('company')}
            className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
            style={{
              color: 'var(--nav-text)',
              ':hover': {
                backgroundColor: 'var(--nav-bg-hover)'
              }
            }}
          >
            <span>{t('nav.company', 'Roba')}</span>
            {openSections.company ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          
          {openSections.company && (
            <div className="mt-2 space-y-1 pl-2">
              <SidebarNavCluster
                title={t('nav.centralWarehouse', 'Magacin')}
                icon={<Package className="h-4 w-4 mr-2" />}
                disabled={!isFeatureAllowed('roba', user)}
                disabledTitle="Roba nije dostupna u ovom paketu"
                collapsible
                open={openSections.warehouse}
                onToggle={() => toggleRobaCluster('warehouse')}
              >
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory')}
                  activePath="/dashboard/company/inventory"
                  icon={<Layout className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.warehouseStock', 'Stanje')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory/inputs')}
                  activePath="/dashboard/company/inventory/inputs"
                  icon={<ArrowDownCircle className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.inventoryInputs', 'Ulaz robe')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory/withdrawn')}
                  activePath="/dashboard/company/inventory/withdrawn"
                  icon={<Undo2 className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.withdrawnItems', 'Povučena roba')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory/reversi')}
                  activePath="/dashboard/company/inventory/reversi"
                  icon={<RotateCcw className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.reversi', 'Reversi')}
                </SidebarNavItem>
              </SidebarNavCluster>

              <SidebarNavCluster
                title={t('nav.spareParts', 'Rezervni delovi')}
                icon={<Wrench className="h-4 w-4 mr-2" />}
                disabled={!isFeatureAllowed('roba', user)}
                disabledTitle="Roba nije dostupna u ovom paketu"
                collapsible
                open={openSections.spareParts}
                onToggle={() => toggleRobaCluster('spareParts')}
              >
                <SidebarNavItem
                  to={getRoutePathWithLocation('spare-parts')}
                  activePath="/dashboard/spare-parts"
                  icon={<Layout className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.sparePartsStock', 'Stanje')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('spare-parts/inputs')}
                  activePath="/dashboard/spare-parts/inputs"
                  icon={<ArrowDownCircle className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.sparePartsInputs', 'Ulaz delova')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('spare-parts/outputs')}
                  activePath="/dashboard/spare-parts/outputs"
                  icon={<ArrowUpCircle className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.sparePartsWithdrawn', 'Povučeni delovi')}
                </SidebarNavItem>
              </SidebarNavCluster>

              <SidebarNavCluster
                title={t('nav.assets', 'Inventar')}
                icon={<ClipboardList className="h-4 w-4 mr-2" />}
                disabled={!isFeatureAllowed('roba', user)}
                disabledTitle="Roba nije dostupna u ovom paketu"
                collapsible
                open={openSections.assets}
                onToggle={() => toggleRobaCluster('assets')}
              >
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventar')}
                  activePath="/dashboard/company/inventar"
                  icon={<Layout className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.assetStock', 'Stanje')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventar/inputs')}
                  activePath="/dashboard/company/inventar/inputs"
                  icon={<ArrowDownCircle className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.assetInputs', 'Ulaz sredstava')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventar/withdrawn')}
                  activePath="/dashboard/company/inventar/withdrawn"
                  icon={<FileMinus className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.assetWriteOff', 'Otpis sredstava')}
                </SidebarNavItem>
              </SidebarNavCluster>

              <SidebarNavCluster
                title={t('nav.robaGroupOther', 'Ostalo')}
                icon={<MoreHorizontal className="h-4 w-4 mr-2" />}
                disabled={!isFeatureAllowed('roba', user)}
                disabledTitle="Roba nije dostupna u ovom paketu"
                collapsible
                open={openSections.ostalo}
                onToggle={() => toggleRobaCluster('ostalo')}
              >
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory/customs')}
                  activePath="/dashboard/company/inventory/customs"
                  icon={<FileText className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.customs', 'Uvoz')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/inventory/calculations')}
                  activePath="/dashboard/company/inventory/calculations"
                  icon={<Calculator className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.calculations', 'Kalkulacije')}
                </SidebarNavItem>
                <SidebarNavItem
                  to={getRoutePathWithLocation('vehicles')}
                  activePath="/dashboard/vehicles"
                  icon={<Car className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('roba', user)}
                  disabledTitle="Roba nije dostupna u ovom paketu"
                >
                  {t('nav.vehicles', 'Vozila')}
                </SidebarNavItem>
              </SidebarNavCluster>

              <div className="pt-2">
                <SidebarNavItem
                  to={getRoutePathWithLocation('company/webshop')}
                  activePath="/dashboard/company/webshop"
                  icon={<ShoppingBag className="h-4 w-4 mr-2" />}
                  disabled={!isFeatureAllowed('webshop', user)}
                  disabledTitle="Web Shop nije dostupan u ovom paketu"
                  variant="cluster"
                >
                  Web Shop
                </SidebarNavItem>
              </div>
            </div>
          )}
            </>
          )}

          {/* Marketing Section - Always visible, but links can be disabled by package */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
                onClick={() => toggleSection('marketing')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>Marketing</span>
                {openSections.marketing ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.marketing && (
                <div className="mt-2 space-y-1 pl-2">
                  <SidebarNavItem
                    to={getRoutePathWithLocation('marketing/loyalty')}
                    activePath="/dashboard/marketing/loyalty"
                    icon={<Gift className="h-4 w-4 mr-2" />}
                    disabled={!isFeatureAllowed('marketing', user)}
                    disabledTitle="Marketing nije dostupan u ovom paketu"
                  >
                    Loyalty
                  </SidebarNavItem>

                  <SidebarNavItem
                    to={getRoutePathWithLocation('marketing/email')}
                    activePath="/dashboard/marketing/email"
                    icon={<Mail className="h-4 w-4 mr-2" />}
                    disabled={!isMarketingChannelAllowed('email', user)}
                    disabledTitle={!isFeatureAllowed('marketing', user) ? 'Marketing nije dostupan u ovom paketu' : 'Mejl marketing dostupan je od Business paketa'}
                  >
                    Mejl
                  </SidebarNavItem>

                  <SidebarNavItem
                    to={getRoutePathWithLocation('marketing/sms')}
                    activePath="/dashboard/marketing/sms"
                    icon={<MessageSquare className="h-4 w-4 mr-2" />}
                    disabled={!isMarketingChannelAllowed('sms', user)}
                    disabledTitle={!isFeatureAllowed('marketing', user) ? 'Marketing nije dostupan u ovom paketu' : 'SMS marketing dostupan je od Business paketa'}
                  >
                    SMS
                  </SidebarNavItem>

                  <SidebarNavItem
                    to={getRoutePathWithLocation('marketing/viber')}
                    activePath="/dashboard/marketing/viber"
                    icon={<MessageCircle className="h-4 w-4 mr-2" />}
                    disabled={!isMarketingChannelAllowed('viber', user)}
                    disabledTitle={!isFeatureAllowed('marketing', user) ? 'Marketing nije dostupan u ovom paketu' : 'Viber marketing dostupan je od Business paketa'}
                  >
                    Viber
                  </SidebarNavItem>

                  <SidebarNavItem
                    to={getRoutePathWithLocation('marketing/whatsapp')}
                    activePath="/dashboard/marketing/whatsapp"
                    icon={<MessageCircle className="h-4 w-4 mr-2" />}
                    disabled={!isMarketingChannelAllowed('whatsapp', user)}
                    disabledTitle={!isFeatureAllowed('marketing', user) ? 'Marketing nije dostupan u ovom paketu' : 'WhatsApp marketing dostupan je od Business paketa'}
                  >
                    WhatsApp
                  </SidebarNavItem>
                </div>
              )}
            </>
          )}

          {/* Tutorijali Section - Hidden for super admin and workers with limited access */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
                onClick={() => toggleSection('tutorijali')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>Tutorijali</span>
                {openSections.tutorijali ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.tutorijali && (
                <div className="mt-2 space-y-1 pl-2">
                  <Link 
                    to={getRoutePathWithLocation('tutorijali/kako-koristiti-app')} 
                    className="flex items-center py-2 px-4 rounded transition duration-200"
                    style={{
                      backgroundColor: isLinkActive('/dashboard/tutorijali/kako-koristiti-app') ? 'var(--nav-active-bg)' : 'transparent',
                      color: isLinkActive('/dashboard/tutorijali/kako-koristiti-app') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                      ':hover': {
                        backgroundColor: 'var(--nav-bg-hover)',
                        color: 'var(--nav-text-hover)'
                      }
                    }}
                  >
                    <Video className="h-4 w-4 mr-2" />
                    {t('nav.tutorialsHowToApp', 'Kako koristiti aplikaciju')}
                  </Link>
                </div>
              )}
            </>
          )}
          
          {/* Package Section - Hidden for super admin and workers with limited access */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
            onClick={() => toggleSection('package')}
            className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
            style={{
              color: 'var(--nav-text)',
              ':hover': {
                backgroundColor: 'var(--nav-bg-hover)'
              }
            }}
          >
            <span>{t('nav.package')}</span>
            {openSections.package ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          
          {openSections.package && (
            <div className="mt-2 space-y-1 pl-2">
              <Link 
                to={getRoutePathWithLocation('packages/starter')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/packages/starter') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/packages/starter') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Zap className="h-4 w-4 mr-2" />
                Starter
              </Link>
              <Link 
                to={getRoutePathWithLocation('packages/standard')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/packages/standard') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/packages/standard') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Award className="h-4 w-4 mr-2" />
                Standard
              </Link>
              <Link 
                to={getRoutePathWithLocation('packages/business')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/packages/business') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/packages/business') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Star className="h-4 w-4 mr-2" />
                Business
              </Link>
              <Link 
                to={getRoutePathWithLocation('packages/premium')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/packages/premium') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/packages/premium') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Star className="h-4 w-4 mr-2 text-yellow-500" />
                Premium
              </Link>
            </div>
          )}
            </>
          )}
          
          {/* Vehicles Section - Only for workers, above Profile */}
          {!isSuperAdmin && isWorker && (
            <>
              <button 
                onClick={() => toggleSection('vehicles')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>{t('nav.vehicles', 'Vozilo')}</span>
                {openSections.vehicles ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.vehicles && (
                <div className="mt-2 space-y-1 pl-2">
                  <Link 
                    to={getRoutePathWithLocation('worker/vehicle')} 
                    className="flex items-center py-2 px-4 rounded transition duration-200"
                    style={{
                      backgroundColor: isLinkActive('/dashboard/worker/vehicle') ? 'var(--nav-active-bg)' : 'transparent',
                      color: isLinkActive('/dashboard/worker/vehicle') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                      ':hover': {
                        backgroundColor: 'var(--nav-bg-hover)',
                        color: 'var(--nav-text-hover)'
                      }
                    }}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    {t('nav.myVehicle', 'Moje vozilo')}
                  </Link>
                </div>
              )}
            </>
          )}
          
          {/* Profile Section - Hidden for super admin, available for all workers */}
          {!isSuperAdmin && (
            <>
              <button 
            onClick={() => toggleSection('profile')}
            className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
            style={{
              color: 'var(--nav-text)',
              ':hover': {
                backgroundColor: 'var(--nav-bg-hover)'
              }
            }}
          >
            <span>{t('nav.profile')}</span>
            {openSections.profile ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          
          {openSections.profile && (
            <div className="mt-2 space-y-1 pl-2">
              <Link 
                to={getRoutePathWithLocation('profile')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/profile') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/profile') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <User className="h-4 w-4 mr-2" />
                {t('nav.myProfile')}
              </Link>
              {/* Company info:
                  - uvek za company korisnike (admin/owner)
                  - za radnike SAMO ako imaju full admin privilegije (hasFullAdminAccess) */}
              {(isCompanyUser || hasFullAdminAccess) && (
              <Link 
                to={getRoutePathWithLocation('company/info')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/company/info') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/company/info') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Briefcase className="h-4 w-4 mr-2" />
                {t('company.info', 'Podaci kompanije')}
              </Link>
              )}
              {/* Upravljanje korisnicima:
                  - uvek za company korisnike
                  - za radnike SAMO ako imaju full admin privilegije */}
              {(isCompanyUser || hasFullAdminAccess) && (
              <Link 
                to={getRoutePathWithLocation('user-management')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/user-management') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/user-management') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <User className="h-4 w-4 mr-2" />
                {t('nav.userManagement')}
              </Link>
              )}
              <div className="px-4 py-2">
                <LanguageSwitcher />
              </div>
              <button 
                onClick={handleLogout}
                className="flex items-center w-full text-left py-2 px-4 rounded text-white/70 hover:bg-white/10 hover:text-white transition duration-200"
              >
                <User className="h-4 w-4 mr-2" />
                {t('auth.logout')}
              </button>
              <div className="flex items-center py-2 px-4 text-white/70">
                <Phone className="h-4 w-4 mr-2" />
                {effectiveUser?.result?.phone || effectiveUser?.phone || 'No phone number'}
              </div>
            </div>
          )}
          
          {/* AI and Parking Settings - Below Profile Section - Only for workers */}
          {!isSuperAdmin && isWorker && (
            <div className="px-4 py-3 mt-2">
              <div className="flex items-center justify-center gap-3">
                {/* AI Icon Toggle */}
                <button
                  onClick={() => setGlobalUseAI(!globalUseAI)}
                  className={`flex items-center justify-center w-10 h-10 rounded transition-all ${
                    globalUseAI
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                  title={globalUseAI ? 'AI asistencija aktivna' : 'AI asistencija neaktivna'}
                >
                  <span className="text-sm font-bold">AI</span>
                </button>
                
                {/* Parking Icon Toggle */}
                <button
                  onClick={() => setGlobalEnableParking(!globalEnableParking)}
                  className={`flex items-center justify-center w-10 h-10 rounded transition-all ${
                    globalEnableParking
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                  title={globalEnableParking ? 'Parking podsetnik aktivan' : 'Parking podsetnik neaktivan'}
                >
                  <span className="text-sm font-bold">P</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Payments Section - Hidden for super admin and workers with limited access */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
                onClick={() => toggleSection('payments')}
                className="flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-6 rounded"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)'
                  }
                }}
              >
                <span>{t('nav.payments')}</span>
                {openSections.payments ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              
              {openSections.payments && (
                <div className="mt-2 space-y-1 pl-2">
                  <Link 
                    to={getRoutePathWithLocation('payments/fakture')} 
                    className="flex items-center py-2 px-4 rounded transition duration-200"
                    style={{
                      backgroundColor: isLinkActive('/dashboard/payments/fakture') ? 'var(--nav-active-bg)' : 'transparent',
                      color: isLinkActive('/dashboard/payments/fakture') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                      ':hover': {
                        backgroundColor: 'var(--nav-bg-hover)',
                        color: 'var(--nav-text-hover)'
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('nav.invoices')}
                  </Link>
                </div>
              )}
            </>
          )}

          {/* AI Section - ispod Plaćanja (uvek vidljiva; funkcionalnost ograničena paketom) */}
          {!isSuperAdmin && !isWorkerWithLimitedAccess && !hasOnlyAssignedJobsAccess && (
            <>
              <button 
                onClick={() => toggleSection('ai')}
                className={`
                  flex items-center justify-between w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider mt-2 
                  rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700
                `}
              >
                <span className="flex-1 text-left">AI</span>
                <span
                  className={`text-[11px] font-semibold ${aiUsageTextColorClass} text-center`}
                  style={{ minWidth: '60px' }}
                >
                  {aiUsage.used}/{aiUsage.limit || 0}
                </span>
                <span className="ml-1">
                  {openSections.ai ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
              </button>
              
              {openSections.ai && (
                <div className="mt-2 space-y-1 pl-2">
                  <SidebarNavItem
                    to={getRoutePathWithLocation('ai-business')}
                    activePath="/dashboard/ai-business"
                    icon={<Brain className="h-4 w-4 mr-2" />}
                  >
                    AI Biznis
                  </SidebarNavItem>
                  <SidebarNavItem
                    to={getRoutePathWithLocation('ai-import')}
                    activePath="/dashboard/ai-import"
                    icon={<FileText className="h-4 w-4 mr-2" />}
                  >
                    AI Import
                  </SidebarNavItem>
                  <SidebarNavItem
                    to={getRoutePathWithLocation('ai-export')}
                    activePath="/dashboard/ai-export"
                    icon={<FileText className="h-4 w-4 mr-2" />}
                  >
                    AI Export
                  </SidebarNavItem>
                  <SidebarNavItem
                    to={getRoutePathWithLocation('ai-settings')}
                    activePath="/dashboard/ai-settings"
                    icon={<Settings className="h-4 w-4 mr-2" />}
                  >
                    AI Podešavanja
                  </SidebarNavItem>
                </div>
              )}

              {/* kvarx — samo firme sa podešenim gradom; poslovi su uvek iz tog grada */}
              {kvarxUnlocked ? (
                <>
              <button
                type="button"
                onClick={() => {
                  setKvarxPing(false);
                  toggleSection('kvarx');
                }}
                className={`mt-2 flex w-full items-center justify-between rounded-lg border px-4 py-2 text-left text-xs font-semibold tracking-wider shadow-lg transition hover:bg-gray-50 ${
                  kvarxPing ? 'animate-pulse border-[#c8102e] bg-red-50' : 'border-gray-200 bg-white'
                }`}
                style={{
                  boxShadow:
                    isLinkActive('/dashboard/kvarx') || isLinkActive('/dashboard/kvarx-archive')
                      ? '0 0 0 2px #c8102e'
                      : undefined,
                }}
              >
                <span className="flex-1 text-left">
                  <span className="text-black">kvar</span>
                  <span className="text-[#c8102e]">x</span>
                  <span className="text-black"> app</span>
                </span>
                <span
                  className="text-center text-[11px] font-semibold text-black"
                  style={{ minWidth: '60px' }}
                  title="Poslovi iz grada firme / ukupno u tom gradu"
                >
                  {kvarxCounts.matched}/{kvarxCounts.total}
                </span>
                <span className="ml-1 text-black">
                  {openSections.kvarx ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
              </button>

              {openSections.kvarx && (
                <div className="mt-2 space-y-1 pl-2">
                  <SidebarNavItem
                    to={getRoutePathWithLocation('kvarx')}
                    activePath="/dashboard/kvarx"
                    icon={<FileText className="h-4 w-4 mr-2" />}
                  >
                    Poslovi
                  </SidebarNavItem>
                  <SidebarNavItem
                    to={getRoutePathWithLocation('kvarx-archive')}
                    activePath="/dashboard/kvarx-archive"
                    icon={<Archive className="h-4 w-4 mr-2" />}
                  >
                    Ugovoreni
                  </SidebarNavItem>
                </div>
              )}
                </>
              ) : null}
            </>
          )}
            </>
          )}
          
          {/* Super Admin Section - Only visible to super admin */}
          {isSuperAdmin && (
            <>
              <Link 
                to={getRoutePathWithLocation('superadmin')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/superadmin') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/superadmin') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('nav.superAdmin')}
              </Link>
              <div className="border-t border-gray-700 my-4"></div>
              <Link 
                to={getRoutePathWithLocation('kompanije')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/kompanije') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/kompanije') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Lista kompanija
              </Link>
              <Link 
                to={getRoutePathWithLocation('poslovanje')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/poslovanje') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/poslovanje') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Poslovanje
              </Link>
              <Link 
                to={getRoutePathWithLocation('kampanje')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/kampanje') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/kampanje') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Megaphone className="h-4 w-4 mr-2" />
                Kampanje
              </Link>
              <Link 
                to={getRoutePathWithLocation('mobilni-telefoni')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/mobilni-telefoni') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/mobilni-telefoni') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Phone className="h-4 w-4 mr-2" />
                Mobilni telefoni
              </Link>
              <Link 
                to={getRoutePathWithLocation('mejl-adrese')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/mejl-adrese') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/mejl-adrese') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Mejl adrese
              </Link>
              <Link 
                to={getRoutePathWithLocation('marketing-saglasnosti')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/marketing-saglasnosti') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/marketing-saglasnosti') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Bell className="h-4 w-4 mr-2" />
                Marketing saglasnosti
              </Link>
              <Link 
                to={getRoutePathWithLocation('prijave-kvarova')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/prijave-kvarova') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/prijave-kvarova') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <Wrench className="h-4 w-4 mr-2" />
                Prijave kvarova
              </Link>
              <Link 
                to={getRoutePathWithLocation('analitika')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/analitika') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/analitika') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analitika
              </Link>
              <Link 
                to={getRoutePathWithLocation('fakturisanje')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/fakturisanje') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/fakturisanje') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Fakturisanje
              </Link>
              <Link 
                to={getRoutePathWithLocation('notifications')} 
                className="flex items-center py-2 px-4 rounded transition duration-200"
                style={{
                  backgroundColor: isLinkActive('/dashboard/notifications') ? 'var(--nav-active-bg)' : 'transparent',
                  color: isLinkActive('/dashboard/notifications') ? 'var(--nav-active-text)' : 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <StickyNote className="h-4 w-4 mr-2" />
                <span>Notifikacije</span>
                {unreadNotifications > 0 && (
                  <span className="ml-2 inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-xs text-white">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </Link>
              <button 
                onClick={handleLogout}
                className="flex items-center w-full text-left py-2 px-4 rounded transition duration-200"
                style={{
                  color: 'var(--nav-text)',
                  ':hover': {
                    backgroundColor: 'var(--nav-bg-hover)',
                    color: 'var(--nav-text-hover)'
                  }
                }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('auth.logout')}
              </button>
              <div className="border-t border-gray-700 my-4"></div>
            </>
          )}
        </nav>
      </div>
      
      {/* Header i glavni sadržaj */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <header 
          className="lg:hidden flex items-center justify-between px-6 py-4 border-b"
          style={{
            backgroundColor: 'var(--nav-bg, #111827)',
            borderColor: 'var(--nav-border, rgba(148, 163, 184, 0.2))',
            color: 'var(--nav-text, #e2e8f0)'
          }}
        >
          <button onClick={() => setSidebarOpen(true)} className="focus:outline-none">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6H20M4 12H20M4 18H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
          <SpinTaskerLogo
            className="text-lg"
            headingClassName="text-lg font-bold text-green-600 flex items-center"
            spinDuration={rotationDuration}
            spinPaused={monkeySpinPaused}
          />
          <NotificationBell />
        </header>
        
        {/* Main content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <main 
            className="flex-1 overflow-x-hidden overflow-y-auto"
            style={{
              backgroundColor: 'var(--dashboard-surface, rgba(15, 23, 42, 0.95))',
              backgroundImage: 'var(--list-bg-image, none)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'var(--dashboard-text, #e2e8f0)',
              minHeight: '100vh'
            }}
          >
            {children}
          </main>
          {/* Footer */}
          <footer 
            className="text-center py-4 border-t"
            style={{
              backgroundColor: 'var(--nav-bg, #111827)',
              borderColor: 'var(--nav-border, rgba(148, 163, 184, 0.2))',
              color: 'var(--nav-text, #e2e8f0)'
            }}
          >
            <p className="text-sm italic font-bold">
              <span className="text-white"><span className="text-xs">b</span>y</span> <span className="text-green-500"><span className="text-xs">S</span>pint</span><span className="text-white mx-0.5 inline-block" style={{ fontSize: '0.67em' }}>@</span><span className="text-green-500">sker</span>
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout; 